import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { CircularProgress } from '@mui/material'
import { selectAuthenticated } from './redux/keycloak/keycloakSelectors'
import routes from './routes'
import Main from './views/layouts/main'
import LoginPage from './views/auth/Login'
import RegisterPage from './views/auth/Register'
import ResetPasswordPage from './views/auth/ResetPassword'
import ForgotPasswordPage from './views/auth/ForgotPassword'
import {
  selectSpecifications,
} from './redux/specification/specificationSelectors'
import { UserRole } from './types/keycloak'
import { hasRole } from './utils/checkRole'
import { RootState, useAppDispatch } from './redux/store'
import { fetchSpecificationsAction } from './redux/specification/specificationAction'
import { fetchLicense } from './redux/license/licenseActions'
import { selectLicenseInitialized } from './redux/license/licenseSelectors'
import { SessionAlertManager } from './components/common/SessionAlerts'
import LicenceExpiree from './components/common/LicenceExpiree'
import { useLicense } from './hooks/useLicense'
import { refreshToken, logoutAndClearStorage } from './redux/keycloak/refreshToken'
import { useTranslation } from 'react-i18next'
import { OnboardingProvider } from './features/onboarding/tour.controller'
import OwnerInfoForm from './views/getStarted/OwnerInfoForm'
import VerifyEmail from './views/getStarted/VerifyEmail'
import WelcomeLanding from './views/getStarted/WelcomeLanding'
import AccountLogin from './views/account/AccountLogin'
import AccountOrganizations from './views/account/AccountOrganizations'
import AccountBilling from './views/account/AccountBilling'
import NotFoundPage from './views/NotFoundPage'
import { logout } from './redux/keycloak/keycloakSlice'

function LogoutRoute() {
  const dispatch = useAppDispatch()
  useEffect(() => {
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('lastRefreshTime')
    dispatch(logout())
    window.location.replace('/login')
  }, [dispatch])
  return null
}

const AuthRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
  </Routes>
)

// Self-service org onboarding + account portal: a completely separate area
// of the app, gated by our own control-plane login (not Keycloak). Rendered
// regardless of tenant (Keycloak) authentication state — see the
// `isControlPlaneRoute` check in App() below.
const CONTROL_PLANE_PATH_PREFIXES = ['/get-started', '/account']

const ControlPlaneRoutes = () => (
  <Routes>
    <Route path="/get-started" element={<OwnerInfoForm />} />
    <Route path="/get-started/verify-email" element={<VerifyEmail />} />
    <Route path="/get-started/welcome" element={<WelcomeLanding />} />
    <Route path="/account/login" element={<AccountLogin />} />
    <Route path="/account/organizations" element={<AccountOrganizations />} />
    <Route path="/account/organizations/:id/billing" element={<AccountBilling />} />
  </Routes>
)

const MainRoutes = ({ specifications }: { specifications: any }) => {
  const roles = useSelector((state: RootState) => state.auth.roles)
  const isRSSI = hasRole(roles, UserRole.RSSI)
  const isAdmin = hasRole(roles, UserRole.ADMIN)
  return (
    <Main
      showSidebar={
        specifications.length === 0 && (isRSSI || isAdmin)
          ? false
          : specifications.length > 0
            ? true
            : true
      }
    >
      <Routes>
        <Route path="/profil1" element={<Navigate to="/profile" replace />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/me" element={<Navigate to="/profile" replace />} />
        <Route path="/myteam" element={<Navigate to="/team" replace />} />
        <Route
          path="/maturite/analysis"
          element={<Navigate to="/maturity/view-analysis/" replace />}
        />
        {routes.map(({ path, component: RouteComponent }) => (
          <Route path={path} element={<RouteComponent />} key={path} />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Main>
  )
}

function App() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const token = localStorage.getItem('token')
  const authenticated = useSelector(selectAuthenticated)
  const roles = useSelector((state: RootState) => state.auth.roles)
  const specifications = useSelector(selectSpecifications)
  const licenseInitialized = useSelector(selectLicenseInitialized)
  const { isExpired, active } = useLicense()

  const [sessionExpiring, setSessionExpiring] = useState(false)
  // True after the post-auth settings fetch settles — prevents select-norm ↔ home flash
  const [settingsReady, setSettingsReady] = useState(false)

  // Token freshness heartbeat
  useEffect(() => {
    if (!authenticated || !token) {
      setSessionExpiring(false)
      return
    }

    const intervalId = setInterval(() => {
      const lastRefreshStr = localStorage.getItem('lastRefreshTime')
      if (!lastRefreshStr) return

      const elapsed = Date.now() - parseInt(lastRefreshStr, 10)
      
      // Auto-logout at 30 mins
      if (elapsed >= 30 * 60 * 1000) {
        setSessionExpiring(false)
        logoutAndClearStorage(t('sessionAlerts.sessionExpired.title'))
      } 
      // Show warning at 25 mins
      else if (elapsed >= 25 * 60 * 1000) {
        setSessionExpiring(true)
      } 
      else {
        setSessionExpiring(false)
      }
    }, 2000)

    return () => clearInterval(intervalId)
  }, [authenticated, token, t])

  const handleRenewSession = async () => {
    try {
      const newAccessToken = await refreshToken()
      if (newAccessToken) {
        setSessionExpiring(false)
      } else {
        logoutAndClearStorage(t('sessionAlerts.sessionExpired.title'))
      }
    } catch {
      logoutAndClearStorage(t('sessionAlerts.sessionExpired.title'))
    }
  }

  useEffect(() => {
    if (!authenticated || !token) {
      setSettingsReady(false)
      return
    }

    let cancelled = false
    setSettingsReady(false)

    dispatch(fetchSpecificationsAction())
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setSettingsReady(true)
      })
    dispatch(fetchLicense())

    return () => {
      cancelled = true
    }
  }, [authenticated, token, dispatch])

  const isControlPlaneRoute = CONTROL_PLANE_PATH_PREFIXES.some((prefix) =>
    window.location.pathname.startsWith(prefix)
  )

  useEffect(() => {
    if (isControlPlaneRoute) return

    if (!authenticated || !token) {
      if (
        window.location.pathname !== '/login' &&
        window.location.pathname !== '/register' &&
        window.location.pathname !== '/forgot-password' &&
        window.location.pathname !== '/reset-password'
      ) {
        navigate('/login', { replace: true })
      }
      return
    }

    // Wait until settings are loaded for this session
    if (!settingsReady) return

    const path = window.location.pathname
    const isRSSI = hasRole(roles, UserRole.RSSI)
    const isAdmin = hasRole(roles, UserRole.ADMIN)
    const hasSettings = Boolean(specifications[0]?.NormBase)
    const isSetupAdmin = isRSSI || isAdmin

    if (isSetupAdmin && !hasSettings) {
      if (path !== '/select-norm') {
        navigate('/select-norm', { replace: true })
      }
      return
    }

    // Configured workspace (or non-setup role): leave login / setup for home
    if (path === '/login' || path === '/select-norm') {
      navigate('/', { replace: true })
    }
  }, [
    authenticated,
    token,
    roles,
    specifications,
    settingsReady,
    navigate,
    isControlPlaneRoute,
  ])

  if (location.pathname === '/logout') {
    return <LogoutRoute />
  }

  return (
    <div>
      <SessionAlertManager
        sessionExpiring={sessionExpiring}
        onRenew={handleRenewSession}
        onLogin={() => { window.location.href = '/login' }}
      />
      {isControlPlaneRoute ? (
        <ControlPlaneRoutes />
      ) : !authenticated || !token ? (
        <AuthRoutes />
      ) : licenseInitialized && (isExpired() || active === false) ? (
        <LicenceExpiree />
      ) : !settingsReady ? (
        <div
          style={{
            minHeight: '100vh',
            background: '#f1f4fa',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <CircularProgress size={36} sx={{ color: '#0d9b87' }} />
        </div>
      ) : (
        <OnboardingProvider enabled={authenticated && Boolean(token) && specifications.length > 0}>
          <MainRoutes specifications={specifications} />
        </OnboardingProvider>
      )}
    </div>
  )
}

export default App
